import type { LibrarySketch } from './types';

export const threeDSketches: LibrarySketch[] = [
  {
    id: '3d-wireframe-sphere',
    name: 'Noisy Wireframe Sphere',
    description: 'An animated wireframe sphere with vertex displacement driven by noise, creating an organic breathing effect.',
    category: '3d',
    tags: ['wireframe', 'sphere', 'noise', '3d', 'organic'],
    runtimeMode: 'threejs',
    inputTypes: [],
    params: [
      { name: 'noiseAmp', type: 'float', min: 0.1, max: 2.0, default: 0.5, label: 'Noise Amount' },
      { name: 'noiseSpeed', type: 'float', min: 0.1, max: 3.0, default: 1.0, label: 'Speed' },
      { name: 'segments', type: 'int', min: 16, max: 64, default: 32, label: 'Detail' },
      { name: 'wireColor', type: 'color', default: '#4488ff', label: 'Color' },
    ],
    code: `// @param float noiseAmp 0.1 2.0 0.5
// @param float noiseSpeed 0.1 3.0 1.0
// @param int segments 16 64 32
// @param color wireColor #4488ff

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(60, genie.width / genie.height, 0.1, 100);
camera.position.z = 4;
const renderer = new THREE.WebGLRenderer({ canvas: genie.canvas, antialias: true });
renderer.setSize(genie.width, genie.height);
renderer.setClearColor(0x000000);
const seg = genie.params.segments ?? 32;
const geo = new THREE.SphereGeometry(1.5, seg, seg);
const col = genie.params.wireColor ?? '#4488ff';
const mat = new THREE.MeshBasicMaterial({ color: col, wireframe: true });
const mesh = new THREE.Mesh(geo, mat);
scene.add(mesh);
const origPos = new Float32Array(geo.attributes.position.array);
function noise3d(x, y, z) {
  return (Math.sin(x * 1.7 + z) + Math.sin(y * 2.3 - x) + Math.sin(z * 1.1 + y * 0.9)) / 3;
}
function animate() {
  requestAnimationFrame(animate);
  const amp = genie.params.noiseAmp ?? 0.5;
  const spd = genie.params.noiseSpeed ?? 1.0;
  const t = performance.now() * 0.001 * spd;
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const ox = origPos[i * 3], oy = origPos[i * 3 + 1], oz = origPos[i * 3 + 2];
    const len = Math.sqrt(ox * ox + oy * oy + oz * oz);
    const nx = ox / len, ny = oy / len, nz = oz / len;
    const n = noise3d(ox * 2 + t, oy * 2 + t * 0.7, oz * 2 + t * 0.3);
    const disp = 1 + n * amp;
    pos.setXYZ(i, nx * len * disp, ny * len * disp, nz * len * disp);
  }
  pos.needsUpdate = true;
  mesh.rotation.y += 0.005;
  mesh.rotation.x += 0.002;
  renderer.render(scene, camera);
}
animate();`,
  },

  {
    id: '3d-raymarching-sdf',
    name: 'Raymarched SDF',
    description: 'GPU raymarching of smooth signed distance field shapes with ambient occlusion and soft shadows.',
    category: '3d',
    tags: ['raymarching', 'sdf', 'gpu', '3d', 'shadows'],
    runtimeMode: 'glsl',
    inputTypes: [],
    params: [
      { name: 'morphSpeed', type: 'float', min: 0.1, max: 3.0, default: 0.8, label: 'Morph Speed' },
      { name: 'smoothness', type: 'float', min: 0.1, max: 2.0, default: 0.5, label: 'Smoothness' },
      { name: 'rotateSpeed', type: 'float', min: 0.0, max: 1.0, default: 0.3, label: 'Rotation' },
    ],
    code: `// @param float morphSpeed 0.1 3.0 0.8
// @param float smoothness 0.1 2.0 0.5
// @param float rotateSpeed 0.0 1.0 0.3

precision mediump float;
uniform vec2 u_resolution;
uniform float u_time;
uniform float morphSpeed;
uniform float smoothness;
uniform float rotateSpeed;

mat2 rot(float a) { float s = sin(a), c = cos(a); return mat2(c,-s,s,c); }

float sdSphere(vec3 p, float r) { return length(p) - r; }
float sdBox(vec3 p, vec3 b) { vec3 d = abs(p) - b; return min(max(d.x,max(d.y,d.z)),0.0) + length(max(d,0.0)); }
float sdTorus(vec3 p, vec2 t) { vec2 q = vec2(length(p.xz) - t.x, p.y); return length(q) - t.y; }
float smin(float a, float b, float k) { float h = clamp(0.5 + 0.5*(a-b)/k, 0.0, 1.0); return mix(a,b,h) - k*h*(1.0-h); }

float scene(vec3 p) {
  float t = u_time * morphSpeed;
  p.xz *= rot(u_time * rotateSpeed);
  p.yz *= rot(u_time * rotateSpeed * 0.7);
  float s = sdSphere(p, 0.8);
  float b = sdBox(p, vec3(0.6));
  float tr = sdTorus(p, vec2(0.8, 0.25));
  float morph = sin(t) * 0.5 + 0.5;
  float k = smoothness;
  return smin(mix(s, b, morph), tr, k);
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.001, 0.0);
  return normalize(vec3(
    scene(p+e.xyy) - scene(p-e.xyy),
    scene(p+e.yxy) - scene(p-e.yxy),
    scene(p+e.yyx) - scene(p-e.yyx)
  ));
}

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5*u_resolution) / u_resolution.y;
  vec3 ro = vec3(0,0,3), rd = normalize(vec3(uv,-1.5));
  float t = 0.0;
  for (int i = 0; i < 80; i++) {
    float d = scene(ro + rd * t);
    if (d < 0.001 || t > 20.0) break;
    t += d;
  }
  vec3 col = vec3(0.02, 0.02, 0.05);
  if (t < 20.0) {
    vec3 p = ro + rd * t;
    vec3 n = calcNormal(p);
    vec3 light = normalize(vec3(1,2,3));
    float diff = max(dot(n, light), 0.0);
    float spec = pow(max(dot(reflect(-light,n),-rd),0.0), 32.0);
    col = vec3(0.2,0.4,0.8) * diff + vec3(1.0) * spec * 0.5 + 0.05;
  }
  gl_FragColor = vec4(col, 1.0);
}`,
  },

  {
    id: '3d-particle-galaxy',
    name: 'Particle Galaxy',
    description: 'A spiral galaxy made of thousands of point particles with logarithmic arm structure and rotation.',
    category: '3d',
    tags: ['galaxy', 'particles', 'space', '3d', 'spiral'],
    runtimeMode: 'threejs',
    inputTypes: [],
    params: [
      { name: 'starCount', type: 'int', min: 1000, max: 20000, default: 8000, label: 'Stars' },
      { name: 'arms', type: 'int', min: 2, max: 6, default: 3, label: 'Arms' },
      { name: 'rotateSpeed', type: 'float', min: 0.01, max: 0.2, default: 0.05, label: 'Rotation' },
      { name: 'spread', type: 'float', min: 0.1, max: 2.0, default: 0.6, label: 'Spread' },
    ],
    code: `// @param int starCount 1000 20000 8000
// @param int arms 2 6 3
// @param float rotateSpeed 0.01 0.2 0.05
// @param float spread 0.1 2.0 0.6

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(60, genie.width / genie.height, 0.1, 500);
camera.position.set(0, 30, 40);
camera.lookAt(0, 0, 0);
const renderer = new THREE.WebGLRenderer({ canvas: genie.canvas, antialias: true });
renderer.setSize(genie.width, genie.height);
renderer.setClearColor(0x000000);
const n = genie.params.starCount ?? 8000;
const armCount = genie.params.arms ?? 3;
const sp = genie.params.spread ?? 0.6;
const positions = new Float32Array(n * 3);
const colors = new Float32Array(n * 3);
for (let i = 0; i < n; i++) {
  const arm = i % armCount;
  const armAngle = (arm / armCount) * Math.PI * 2;
  const dist = Math.pow(Math.random(), 0.5) * 30;
  const spiralAngle = dist * 0.4 + armAngle;
  const scatter = (Math.random() - 0.5) * sp * dist * 0.2;
  const x = Math.cos(spiralAngle) * dist + scatter;
  const z = Math.sin(spiralAngle) * dist + scatter;
  const y = (Math.random() - 0.5) * sp * 2 * Math.exp(-dist * 0.05);
  positions[i * 3] = x;
  positions[i * 3 + 1] = y;
  positions[i * 3 + 2] = z;
  const brightness = 0.5 + Math.random() * 0.5;
  const tint = dist / 30;
  colors[i * 3] = brightness * (1 - tint * 0.3);
  colors[i * 3 + 1] = brightness * (0.7 + tint * 0.3);
  colors[i * 3 + 2] = brightness;
}
const geo = new THREE.BufferGeometry();
geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
const mat = new THREE.PointsMaterial({ size: 0.15, vertexColors: true, sizeAttenuation: true });
const points = new THREE.Points(geo, mat);
scene.add(points);
function animate() {
  requestAnimationFrame(animate);
  const rs = genie.params.rotateSpeed ?? 0.05;
  points.rotation.y += rs * 0.01;
  camera.position.x = Math.sin(performance.now() * 0.0001) * 10;
  camera.lookAt(0, 0, 0);
  renderer.render(scene, camera);
}
animate();`,
  },

  {
    id: '3d-rotating-torus-knot',
    name: 'Iridescent Torus Knot',
    description: 'A rotating torus knot with iridescent normal-based coloring and smooth Phong shading.',
    category: '3d',
    tags: ['torus', 'knot', 'iridescent', '3d', 'shading'],
    runtimeMode: 'threejs',
    inputTypes: [],
    params: [
      { name: 'p', type: 'int', min: 1, max: 10, default: 3, label: 'P Wraps' },
      { name: 'q', type: 'int', min: 1, max: 10, default: 5, label: 'Q Wraps' },
      { name: 'tubeRadius', type: 'float', min: 0.1, max: 1.0, default: 0.3, label: 'Tube Radius' },
      { name: 'rotateSpeed', type: 'float', min: 0.1, max: 2.0, default: 0.5, label: 'Speed' },
    ],
    code: `// @param int p 1 10 3
// @param int q 1 10 5
// @param float tubeRadius 0.1 1.0 0.3
// @param float rotateSpeed 0.1 2.0 0.5

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(50, genie.width / genie.height, 0.1, 100);
camera.position.z = 5;
const renderer = new THREE.WebGLRenderer({ canvas: genie.canvas, antialias: true });
renderer.setSize(genie.width, genie.height);
renderer.setClearColor(0x0a0a0a);
const pp = genie.params.p ?? 3;
const qq = genie.params.q ?? 5;
const tr = genie.params.tubeRadius ?? 0.3;
const geo = new THREE.TorusKnotGeometry(1.5, tr, 200, 32, pp, qq);
const mat = new THREE.ShaderMaterial({
  vertexShader: \`
    varying vec3 vNormal;
    varying vec3 vViewPos;
    void main() {
      vNormal = normalize(normalMatrix * normal);
      vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
      vViewPos = -mvPos.xyz;
      gl_Position = projectionMatrix * mvPos;
    }
  \`,
  fragmentShader: \`
    varying vec3 vNormal;
    varying vec3 vViewPos;
    void main() {
      vec3 n = normalize(vNormal);
      vec3 v = normalize(vViewPos);
      float fresnel = pow(1.0 - max(dot(n, v), 0.0), 3.0);
      vec3 baseColor = 0.5 + 0.5 * cos(n * 3.0 + vec3(0.0, 2.1, 4.2));
      vec3 light = normalize(vec3(1.0, 2.0, 3.0));
      float diff = max(dot(n, light), 0.0) * 0.7 + 0.3;
      vec3 col = baseColor * diff + vec3(1.0, 0.9, 0.8) * fresnel * 0.5;
      gl_FragColor = vec4(col, 1.0);
    }
  \`
});
const mesh = new THREE.Mesh(geo, mat);
scene.add(mesh);
function animate() {
  requestAnimationFrame(animate);
  const rs = genie.params.rotateSpeed ?? 0.5;
  mesh.rotation.x += 0.005 * rs;
  mesh.rotation.y += 0.01 * rs;
  renderer.render(scene, camera);
}
animate();`,
  },
];
